import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { palette, radii, shadow } from '../../styles/tokens';

/**
 * DatePicker — admin twin (mobile uses DatePickerField + native pickers).
 *
 * Storage layer is always 'YYYY-MM-DD'; never a Date object. Display layer
 * formats via Intl. Calendar grid + keyboard nav are hand-rolled against
 * tokens to keep the dependency surface tight (Rule #17).
 *
 * Q6 Phase 1.1 (Session 78).
 */

export interface DatePickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  clearable?: boolean;
  autoFocus?: boolean;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayISO(): string {
  return toISO(new Date());
}

function parseISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const DISPLAY_FMT = new Intl.DateTimeFormat('en-CA', {
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
});

function formatDisplay(s: string): string {
  const d = parseISO(s);
  return d ? DISPLAY_FMT.format(d) : '';
}

function clamp(s: string, min?: string, max?: string): string {
  if (min && s < min) return min;
  if (max && s > max) return max;
  return s;
}

function buildMonthGrid(year: number, month: number): (string | null)[][] {
  const firstDay = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const rows: (string | null)[][] = [];
  let week: (string | null)[] = [];
  for (let i = 0; i < firstDay; i++) week.push(null);
  for (let d = 1; d <= days; d++) {
    week.push(`${year}-${pad(month + 1)}-${pad(d)}`);
    if (week.length === 7) { rows.push(week); week = []; }
  }
  while (week.length > 0 && week.length < 7) week.push(null);
  if (week.length > 0) rows.push(week);
  return rows;
}

const WRAPPER: React.CSSProperties = { position: 'relative', width: '100%' };

const ANCHOR: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '9px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  cursor: 'pointer',
  textAlign: 'left',
  outline: 'none',
  boxSizing: 'border-box',
};

const ANCHOR_DISABLED: React.CSSProperties = {
  background: palette.g50,
  color: palette.g400,
  cursor: 'not-allowed',
};

const ANCHOR_ERROR: React.CSSProperties = { borderColor: palette.red };

const ANCHOR_LABEL: React.CSSProperties = { flex: 1, color: palette.g800 };
const ANCHOR_PLACEHOLDER: React.CSSProperties = { flex: 1, color: palette.g400 };

const CLEAR_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: 9,
  border: 'none',
  background: 'transparent',
  color: palette.g500,
  cursor: 'pointer',
  padding: 0,
};

const POPOVER: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  background: palette.white,
  border: `1px solid ${palette.g200}`,
  borderRadius: radii.md,
  boxShadow: shadow.md,
  padding: 12,
  width: 280,
  zIndex: 30,
};

const POPOVER_FLIP: React.CSSProperties = {
  ...POPOVER,
  top: 'auto',
  bottom: 'calc(100% + 4px)',
};

const HEADER: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8,
};

const NAV_BTN: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 6, border: 'none',
  background: 'transparent', color: palette.g600, cursor: 'pointer',
};

const MONTH_LABEL: React.CSSProperties = {
  fontSize: 13.5, fontWeight: 600, color: palette.g800,
};

const GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2,
};

const DAY_HEADER: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: palette.g500, textAlign: 'center',
  padding: '4px 0', textTransform: 'uppercase', letterSpacing: 0.5,
};

const CELL: React.CSSProperties = {
  height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: 'none', background: 'transparent', borderRadius: 6,
  fontSize: 13, color: palette.g800, cursor: 'pointer', fontFamily: 'inherit',
  padding: 0,
};

const CELL_EMPTY: React.CSSProperties = { ...CELL, cursor: 'default' };
const CELL_TODAY: React.CSSProperties = { ...CELL, boxShadow: `inset 0 0 0 1.5px ${palette.accent}`, color: palette.accentDeep, fontWeight: 600 };
const CELL_SELECTED: React.CSSProperties = { ...CELL, background: palette.accent, color: palette.white, fontWeight: 600 };
const CELL_DISABLED: React.CSSProperties = { ...CELL, color: palette.g300, cursor: 'not-allowed' };

const FOOTER: React.CSSProperties = {
  marginTop: 8, paddingTop: 8, borderTop: `1px solid ${palette.g100}`,
  display: 'flex', justifyContent: 'flex-end',
};

const TODAY_BTN: React.CSSProperties = {
  border: 'none', background: 'transparent', color: palette.accentDeep,
  fontWeight: 500, fontSize: 12.5, cursor: 'pointer', padding: '4px 8px',
  borderRadius: 6, fontFamily: 'inherit',
};

const ERROR_TEXT: React.CSSProperties = { fontSize: 12, color: palette.red, marginTop: 4 };

export function DatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = 'Select date',
  disabled = false,
  error,
  clearable = true,
  autoFocus = false,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const initialFocus = useMemo(() => {
    const start = value ?? clamp(todayISO(), minDate, maxDate);
    return parseISO(start) ?? new Date();
  }, [value, minDate, maxDate]);

  const [viewYear, setViewYear] = useState(initialFocus.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialFocus.getMonth());
  const [focused, setFocused] = useState<string>(value ?? clamp(todayISO(), minDate, maxDate));

  useEffect(() => {
    if (autoFocus) anchorRef.current?.focus();
  }, [autoFocus]);

  /* eslint-disable react-hooks/set-state-in-effect -- sync calendar view to focused date when popover opens or focus moves */
  useEffect(() => {
    if (!open) return;
    const f = parseISO(focused);
    if (f) { setViewYear(f.getFullYear()); setViewMonth(f.getMonth()); }
  }, [open, focused]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- popover flip-up needs viewport measurement, only runs on open transition
    setFlip(spaceBelow < 360 && rect.top > 360);
  }, [open]);

  const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const isDisabledDate = useCallback((s: string) => {
    if (minDate && s < minDate) return true;
    if (maxDate && s > maxDate) return true;
    return false;
  }, [minDate, maxDate]);

  const commit = useCallback((s: string) => {
    if (isDisabledDate(s)) return;
    onChange(s);
    setOpen(false);
  }, [isDisabledDate, onChange]);

  const moveFocus = useCallback((deltaDays: number) => {
    const f = parseISO(focused);
    if (!f) return;
    const next = new Date(f);
    next.setDate(next.getDate() + deltaDays);
    setFocused(toISO(next));
  }, [focused]);

  const moveMonth = useCallback((deltaMonths: number) => {
    const f = parseISO(focused) ?? new Date();
    const next = new Date(f);
    next.setMonth(next.getMonth() + deltaMonths);
    setFocused(toISO(next));
  }, [focused]);

  const handleKey = useCallback((e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowLeft': e.preventDefault(); moveFocus(-1); break;
      case 'ArrowRight': e.preventDefault(); moveFocus(1); break;
      case 'ArrowUp': e.preventDefault(); moveFocus(-7); break;
      case 'ArrowDown': e.preventDefault(); moveFocus(7); break;
      case 'PageUp': e.preventDefault(); moveMonth(-1); break;
      case 'PageDown': e.preventDefault(); moveMonth(1); break;
      case 'Home': {
        e.preventDefault();
        const f = parseISO(focused); if (!f) break;
        const next = new Date(f); next.setDate(f.getDate() - f.getDay());
        setFocused(toISO(next));
        break;
      }
      case 'End': {
        e.preventDefault();
        const f = parseISO(focused); if (!f) break;
        const next = new Date(f); next.setDate(f.getDate() + (6 - f.getDay()));
        setFocused(toISO(next));
        break;
      }
      case 'Enter': e.preventDefault(); commit(focused); break;
      case 'Escape': e.preventDefault(); setOpen(false); break;
    }
  }, [open, focused, moveFocus, moveMonth, commit]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  }, [onChange]);

  const goToday = useCallback(() => {
    const t = clamp(todayISO(), minDate, maxDate);
    setFocused(t);
    if (!isDisabledDate(t)) commit(t);
  }, [minDate, maxDate, isDisabledDate, commit]);

  const today = todayISO();
  const popoverStyle = flip ? POPOVER_FLIP : POPOVER;
  const showClear = clearable && !!value && !disabled;

  return (
    <div style={WRAPPER} ref={wrapperRef}>
      <button
        ref={anchorRef}
        type="button"
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="date-picker-popover"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleKey}
        style={{
          ...ANCHOR,
          ...(error ? ANCHOR_ERROR : null),
          ...(disabled ? ANCHOR_DISABLED : null),
        }}
      >
        <Calendar size={14} color={disabled ? palette.g400 : palette.g500} />
        <span style={value ? ANCHOR_LABEL : ANCHOR_PLACEHOLDER}>
          {value ? formatDisplay(value) : placeholder}
        </span>
        {showClear && (
          // role="button" span replaces nested <button> per §Wave-A Phase E.
          // Outer combobox is already a <button>; nesting a button inside it
          // is invalid DOM (React DevTools warning). Span+role+tabIndex+
          // keyboard handler preserves all a11y semantics: keyboard tab
          // reaches it, Enter/Space triggers clear, screen readers announce
          // it as a button via aria-label. handleClear preserved as-is
          // (already calls e.stopPropagation to prevent the outer combobox
          // from toggling open on inner click).
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear date"
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }
            }}
            style={CLEAR_BTN}
          >
            <X size={12} />
          </span>
        )}
      </button>

      {open && (
        <div
          id="date-picker-popover"
          role="dialog"
          aria-label="Choose date"
          style={popoverStyle}
          onKeyDown={handleKey}
          tabIndex={-1}
        >
          <div style={HEADER}>
            <button
              type="button"
              aria-label="Previous month"
              style={NAV_BTN}
              onClick={() => moveMonth(-1)}
            >
              <ChevronLeft size={16} />
            </button>
            <div style={MONTH_LABEL}>{MONTH_NAMES[viewMonth]} {viewYear}</div>
            <button
              type="button"
              aria-label="Next month"
              style={NAV_BTN}
              onClick={() => moveMonth(1)}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div style={GRID} role="grid" aria-label="Calendar">
            {DAY_LABELS.map((d) => (
              <div key={d} style={DAY_HEADER}>{d.slice(0, 1)}</div>
            ))}
            {grid.flat().map((ds, i) => {
              if (!ds) return <div key={`e-${i}`} style={CELL_EMPTY} />;
              const disabledCell = isDisabledDate(ds);
              const isSel = ds === value;
              const isToday = ds === today;
              const isFocused = ds === focused;
              const style = disabledCell ? CELL_DISABLED
                : isSel ? CELL_SELECTED
                : isToday ? CELL_TODAY
                : CELL;
              return (
                <button
                  key={ds}
                  type="button"
                  role="gridcell"
                  aria-selected={isSel}
                  aria-disabled={disabledCell}
                  data-testid={`date-cell-${ds}`}
                  data-focused={isFocused ? 'true' : undefined}
                  disabled={disabledCell}
                  onClick={() => commit(ds)}
                  style={style}
                >
                  {parseInt(ds.split('-')[2], 10)}
                </button>
              );
            })}
          </div>

          <div style={FOOTER}>
            <button type="button" style={TODAY_BTN} onClick={goToday}>Today</button>
          </div>
        </div>
      )}

      {error && <div style={ERROR_TEXT}>{error}</div>}
    </div>
  );
}
